import React, { useState, useEffect } from 'react';
import { Card, Tag, Progress, Button, Modal, message, Alert } from 'antd';
import { DealListDto, DealStage } from 'api/models';
import { formatCurrency, formatDate, formatDateTime } from 'utility/utility';
import FeatherIcon from 'feather-icons-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { DealStageConfig } from '../constants';
import { t } from 'i18next';
import dayjs from 'dayjs';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useInView } from 'react-intersection-observer';

const Board = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 8px 4px 16px 4px;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f0f0f0;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #bfbfbf;
  }
`;

const Column = styled(Card)`
  min-width: 350px;
  background: #f8f9fa;
  margin-bottom: 8px;
  border-radius: 6px;

  .ant-card-head {
    min-height: unset;
    padding: 12px 16px;
    border-bottom: none;
  }

  .ant-card-body {
    padding: 16px;
  }
`;

const ColumnTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StageName = styled.span`
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #1f2937 !important;
`;

const Revenue = styled.span`
  font-size: 15px !important;
  color: #059669 !important;
  font-weight: 600 !important;
`;

const DealCount = styled.span`  
  font-size: 13px !important;
  color: #6b7280 !important;
`;

const DealCard = styled(Card)`
  margin-bottom: 16px;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .ant-card-body {
    padding: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ClientName = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 6px;
`;

const ProjectInfo = styled.div`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 12px;
`;

const UnitInfo = styled.div`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UnitCount = styled.span`
  font-weight: 500;
`;

const UnitNames = styled.span`
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const CardStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;

  .ant-tag {
    font-size: 14px;
    padding: 4px 8px;
  }
`;

const PaymentInfo = styled.div`
  margin: 12px 0;

  .ant-progress {
    margin-bottom: 8px;
  }
`;

const NextPayment = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const WarningIcon = styled(FeatherIcon)`
  color: #faad14;
`;

interface Props {
  deals: DealListDto[];
  loading: boolean;
  onStageChange?: (dealId: number, newStage: DealStage) => Promise<void>;
  onDelete?: (dealId: number) => Promise<void>;
  onLoadMore?: () => void;
  hasMore: boolean;
}

const DealKanbanBoard: React.FC<Props> = ({ deals, loading, onStageChange, onDelete, onLoadMore, hasMore }) => {
  const getDealsByStage = (stage: DealStage) => deals.filter(deal => deal.stage === stage);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const dealId = parseInt(result.draggableId);
    const newStage = parseInt(result.destination.droppableId) as DealStage;
    const deal = deals.find(d => d.id === dealId);

    if (!deal) return;

    if (deal.stage === newStage) return;

    // Show warnings for specific stage transitions
    if (newStage === DealStage.ClosedWon) {
      const hasNoPayments = !deal.paidAmount || deal.paidAmount === 0;
      const showUnpaidDealTotalWarning =
        deal.totalAmount && deal.totalAmount > 0 && deal.totalAmount !== deal.paidAmount;
      const showUnpaidPaymentsWarning =
        deal?.nextPaymentDue !== '0001-01-01T00:00:00' &&
        deal?.nextPaymentAmount &&
        (deal?.totalAmount ?? 0) !== (deal?.paidAmount ?? 0);

      if (hasNoPayments) {
        Modal.confirm({
          title: t('deals:warning', 'Warning'),
          content: t(
            'deals:no-payments-warning',
            'You are about to mark this deal as won, but no payment schedule has been added.',
          ),
          icon: <WarningIcon icon="alert-triangle" />,
          okText: t('global:continue', 'Continue'),
          cancelText: t('global:cancel', 'Cancel'),
          onOk: () => updateDealStage(dealId, newStage),
        });
        return;
      }

      if (showUnpaidDealTotalWarning) {
        Modal.confirm({
          title: t('deals:warning', 'Warning'),
          content: t(
            'deals:unpaid-payments-warning',
            'You are about to mark this deal as won, but the deal total amount is not fully paid yet.',
          ),
          icon: <WarningIcon icon="alert-triangle" />,
          okText: t('global:continue', 'Continue'),
          cancelText: t('global:cancel', 'Cancel'),
          onOk: () => updateDealStage(dealId, newStage),
        });
        return;
      }

      if (showUnpaidPaymentsWarning) {
        Modal.confirm({
          title: t('deals:warning', 'Warning'),
          content: t(
            'deals:unpaid-scheduled-payments-warning',
            'You are about to mark this deal as won, but there are unpaid scheduled payments.',
          ),
          icon: <WarningIcon icon="alert-triangle" />,
          okText: t('global:continue', 'Continue'),
          cancelText: t('global:cancel', 'Cancel'),
          onOk: () => updateDealStage(dealId, newStage),
        });
        return;
      }
    }

    // If no warnings or user confirmed, proceed with the update
    await updateDealStage(dealId, newStage);
  };

  const updateDealStage = async (dealId: number, newStage: DealStage) => {
    try {
      await onStageChange?.(dealId, newStage);

      message.success(t('deals:stage-updated', 'Deal stage updated successfully'));
    } catch (error) {
      message.error(t('deals:update-error', 'Failed to update deal stage'));
    }
  };

  const handleDelete = async (dealId: number) => {
    Modal.confirm({
      title: t('deals:confirm-delete', 'Confirm Delete'),
      content: t('deals:delete-warning', 'Are you sure you want to delete this deal? This action cannot be undone.'),
      icon: <WarningIcon icon="alert-triangle" />,
      okText: t('global:delete', 'Delete'),
      okButtonProps: { danger: true },
      cancelText: t('global:cancel', 'Cancel'),
      onOk: async () => {
        try {
          await onDelete?.(dealId);
          message.success(t('deals:delete-success', 'Deal deleted successfully'));
        } catch (error) {
          message.error(t('deals:delete-error', 'Failed to delete deal'));
        }
      },
    });
  };

  // Add intersection observer hook
  const { ref, inView } = useInView({
    threshold: 0.5,
  });

  // Trigger load more when the loader comes into view
  useEffect(() => {
    if (inView && hasMore && !loading) {
      onLoadMore?.();
    }
  }, [inView, hasMore, loading]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Board>
        {Object.entries(DealStageConfig).map(([stage, { key, title, color }]) => (
          <Droppable key={stage} droppableId={stage}>
            {(provided, snapshot) => (
              <Column
                ref={provided.innerRef}
                {...provided.droppableProps}
                loading={loading}
                title={
                  <ColumnTitle>
                    <StageHeader>
                      <StageName>{t(key, title)}</StageName>
                      <Revenue>
                        {formatCurrency(
                          getDealsByStage(parseInt(stage) as DealStage).reduce(
                            (sum, deal) => sum + (deal.totalAmount || 0),
                            0,
                          ),
                        )}
                      </Revenue>
                    </StageHeader>
                    <DealCount>{t('side-bar:deals', 'Deals')}: {getDealsByStage(parseInt(stage) as DealStage).length}</DealCount>
                  </ColumnTitle>
                }
              >
                {getDealsByStage(parseInt(stage) as DealStage).map((deal, index) => (
                  <Draggable key={deal.id} draggableId={deal.id!.toString()} index={index}>
                    {(provided, snapshot) => (
                      <DealCard
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        size="small"
                        loading={loading}
                      >
                        <CardHeader>
                          <div>
                            <ClientName>{deal.clientName}</ClientName>
                            <ProjectInfo>
                              <div>{deal.projectName}</div>
                              <UnitInfo>
                                <UnitCount>{t('global:units', 'Units')}: {deal.unitsCount}</UnitCount>
                                {deal.unitNames && deal.unitNames.length > 0 && (
                                  <UnitNames title={deal.unitNames.join(', ')}>
                                    ({deal.unitNames.length > 2 
                                      ? `${deal.unitNames[0]}, ${deal.unitNames[1]}...` 
                                      : deal.unitNames.join(', ')})
                                  </UnitNames>
                                )}
                              </UnitInfo>
                            </ProjectInfo>
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {deal.isActive && !deal.closedAt && (!deal.paidAmount || deal.paidAmount === 0) && (
                              <>
                                <Link to={`/deals/${deal.id}/edit`}>
                                  <Button type="text" icon={<FeatherIcon icon="edit-2" size={16} />} />
                                </Link>
                                {(deal.stage === DealStage.InProgress || deal.stage === DealStage.ClosedLost) && (
                                  <Button
                                    type="text"
                                    danger
                                    icon={<FeatherIcon icon="trash-2" size={16} />}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleDelete(deal.id!);
                                    }}
                                  />
                                )}
                              </>
                            )}
                            <Link to={`/deals/${deal.id}`}>
                              <Button type="text" icon={<FeatherIcon icon="external-link" size={16} />} />
                            </Link>
                          </div>
                        </CardHeader>

                        <PaymentInfo>
                          <Progress
                            percent={Math.round((deal.paidAmount! / deal.totalAmount!) * 100)}
                            size="small"
                            status={deal.paidAmount === deal.totalAmount ? 'success' : 'active'}
                          />
                          {deal.stage === DealStage.InProgress && (
                            <NextPayment>
                              {deal.nextPaymentDue && deal.nextPaymentDue !== '0001-01-01T00:00:00' ? (
                                <>
                                  <FeatherIcon icon="calendar" size={16} />
                                  {t('global:next', 'Next')}: {dayjs(deal.nextPaymentDue).format('YYYY-MM-DD')} (
                                  {formatCurrency(deal.nextPaymentAmount)})
                                </>
                              ) : !deal.paidAmount || deal.paidAmount === 0 ? (
                                <Alert
                                  message={t('deals:no-payment-schedule', 'No payment schedule set')}
                                  type="warning"
                                  showIcon
                                  style={{ padding: '2px 8px', fontSize: '13px' }}
                                />
                              ) : null}
                            </NextPayment>
                          )}
                        </PaymentInfo>

                        <CardStats>
                          <Tag color="blue">{formatCurrency(deal.totalAmount)}</Tag>
                          <span style={{ fontSize: '13px', color: 'rgba(0,0,0,0.55)' }}>
                            {formatDateTime(deal.createdAt)}
                          </span>
                        </CardStats>
                      </DealCard>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}

                {/* Add loader reference to the last column */}
                {stage === Object.keys(DealStageConfig)[Object.keys(DealStageConfig).length - 1] && (
                  <div ref={ref} style={{ height: '20px', margin: '8px 0' }}>
                    {loading && <div style={{ textAlign: 'center' }}>{t('global:loading', 'Loading...')}</div>}
                  </div>
                )}
              </Column>
            )}
          </Droppable>
        ))}
      </Board>
    </DragDropContext>
  );
};

export default DealKanbanBoard;
